/* eslint-disable import/no-cycle */
import { DataProvider } from "@shared/constants/common";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";

import AuthorizePaymentGateway from "../../gateways/AuthorizePaymentGateway/AuthorizePaymentGateway";
import StripePaymentGateway from "../../gateways/StripePaymentGateway/StripePaymentGateway";
import YunoPaymentGateway from "../../gateways/YunoPaymentGateway/YunoPaymentGateway";
import PaymentGatewayFactory from "../../PaymentGatewayFactory";
import { type PaymentManagerConfig } from "../../PaymentManager";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "../../services/useBasketPaymentService";
import useInvoicePaymentService from "../../services/useInvoicePaymentService";
import { PurchaseOptsType } from "../../types";
import PaymentHandler from "../PaymentHandler";

import { PaymentConfigurationError } from "../../utils/errors/PaymentConfigurationError";
import { PaymentInitializationError } from "../../utils/errors/PaymentInitializationError";
import { PaymentProcessingError } from "../../utils/errors/PaymentProcessingError";
import PaymentGateway from "../../gateways/PaymentGateway";

/**
 * The default payment handler for regular purchases.
 * Regular signifies that it is not a waitlist basket, not a free basket, and is not
 * a BNPL basket like Affirm
 */
class RegularPaymentHandler extends PaymentHandler {
  public static handlerName = "RegularPaymentHandler";

  HAS_ASYNCHRONOUS_CONFIRMATION = true;
  HAS_ASYNCHRONOUS_CAPTURE = true;

  is_initialized = false;
  gateway:
    | StripePaymentGateway
    | AuthorizePaymentGateway
    | YunoPaymentGateway
    | null = null;

  constructor(
    config: PaymentManagerConfig,
    basket: PaymentServiceBasket,
    servicesContext: {
      basketPaymentService: ReturnType<typeof useBasketPaymentService>;
      invoicePaymentService: ReturnType<typeof useInvoicePaymentService>;
    }
  ) {
    super(config, basket, servicesContext);

    const selectedGateway = PaymentGatewayFactory.getOrCreateGateway(
      config,
      basket,
      servicesContext
    );

    // Check that the gateway that was selected is supported by this handler
    if (
      !(
        selectedGateway instanceof StripePaymentGateway ||
        selectedGateway instanceof AuthorizePaymentGateway ||
        selectedGateway instanceof YunoPaymentGateway
      )
    ) {
      throw new PaymentConfigurationError(
        `${RegularPaymentHandler.name} does not support the selected gateway ${selectedGateway.constructor.name}`,
        {
          metadata: {
            gatewayName: selectedGateway.constructor.name,
          },
        }
      );
    }

    this.gateway = selectedGateway;
  }

  async initialize(basket: PaymentServiceBasket) {
    if (!this.gateway) {
      throw new PaymentInitializationError(
        "Tried to initialize a handler without its gateway set"
      );
    }

    await this.gateway.initialize(basket);

    this.is_initialized = true;
    return this;
  }

  async processCheckout(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.gateway) {
      throw new PaymentProcessingError(
        "Tried to process a checkout without a gateway set on the handler",
        {
          metadata: {
            operation: "processCheckout",
            handlerType: "RegularPaymentHandler",
          },
          userMessage:
            "Unable to process payment: Payment gateway not configured",
        }
      );
    }

    if (this.gateway instanceof StripePaymentGateway) {
      await this.gateway.confirmPayment(
        confirmationStrategy,
        basketType,
        basket,
        purchaseOpts
      );
    } else if (this.gateway instanceof AuthorizePaymentGateway) {
      await this.gateway.confirmPayment(
        confirmationStrategy,
        basketType,
        basket,
        purchaseOpts
      );
    } else if (this.gateway instanceof YunoPaymentGateway) {
      await this.gateway.confirmPayment(
        confirmationStrategy,
        basketType,
        basket,
        purchaseOpts
      );
    } else {
      throw new PaymentConfigurationError(
        "Tried to process a checkout without an applicable PaymentGateway",
        {
          metadata: {
            gatewayType:
              (this.gateway as PaymentGateway)?.constructor?.name || "unknown",
            operation: "processCheckout",
          },
        }
      );
    }

    return this;
  }

  async postProcessCheckout() {
    if (!this.gateway) {
      throw new PaymentProcessingError(
        "Tried to post process a checkout without a gateway set on the handler",
        {
          metadata: {
            operation: "postProcessCheckout",
            handlerType: "RegularPaymentHandler",
          },
          userMessage:
            "Unable to complete payment: Payment gateway not configured",
        }
      );
    }

    return this.gateway.postProcessCheckout();
  }
}

export default RegularPaymentHandler;
